/**
 * O conteúdo de fábrica de "Personagem" — o resumo da ficha do Mistik Island.
 *
 * <p><b>De onde cada linha veio.</b> Cinco fontes, e elas não concordam:
 *
 * <ul>
 *   <li><b>épico-03</b> — `docs/cards-beta/epico-03-personagem-condicoes-carga.md` no
 *       repositório do jogo: atributos, condições, carga, reputação e o atributo
 *       efetivo.</li>
 *   <li><b>épico-04</b> — `epico-04-inventario-itens-equipamento.md`: inventário,
 *       encaixes, cinto, consumo e comida.</li>
 *   <li><b>épico-05</b> — `epico-05-pericias-progressao.md`: perícias, o teste e o XP.</li>
 *   <li><b>GDD v3.3</b> — `GDD_MistikIsland_v3.3.html`, o cânone de design. Ele declara
 *       que número de balanceamento não mora nele — onde só a estrutura é canônica, a
 *       célula diz isso.</li>
 *   <li><b>a tela Ficha</b> — a lista de perícias e os seis atributos que o Theo já
 *       fixou no próprio GG Pilot (`atributos.ts`, `pericias.ts`).</li>
 * </ul>
 *
 * <p><b>As decisões de 19/08/2026 já estão aplicadas.</b> O Theo fechou, em conversa:
 * carga com Vitalidade (a fórmula do código), Sono fora da lista de condições (como o
 * GDD v3.3), Energia no modelo do GDD (orçamento do dia), e o catálogo de perícias como
 * união das fontes sem duplicar — Magia Defensiva absorve o Escudo Arcano, Magia
 * Ofensiva absorve o "Combater com Magia". As tabelas mostram o que vale; o quadro de
 * conflitos guarda o registro e o que ainda está aberto.
 *
 * <p><b>Duas abas, dois documentos.</b> A página se divide em Beta/Season 1
 * ({@link TABELAS}/{@link SECOES}, chave `personagem-resumo`) e Season 2+
 * ({@link TABELAS_S2}/{@link SECOES_S2}, chave `personagem-season2`) — pedido do Theo,
 * 19/08/2026: a primeira aba enxuta, só com o que vai até a Season 1; o que foi marcado
 * para depois mora inteiro na segunda. Não há mais checkboxes de entrega aqui — o
 * recorte de temporada É a aba.
 *
 * <p><b>Por que o conteúdo mora em código e não numa migration de seed.</b> Isto é o
 * ponto de partida da conversa, não dado do produto: o que o time gravar por cima vence
 * inteiro, e este arquivo vira só o que a página mostra a quem a abre pela primeira vez.
 *
 * <p><b>O que esta página NÃO cobre, de propósito:</b> o catálogo completo de tipos de
 * cena e ações — ele mora em "Cenas, Ações e Fluxo de Jogo", com os conflitos dele. O
 * quadro de ações daqui é um recorte: só o que mexe na ficha (decisão do Theo,
 * 19/08/2026, contra duplicar as duas tabelas). E <b>as condições</b> — Vigor,
 * Concentração, Carga, Energia, dormida, descanso, Fome, Frio, Cansaço e os status de
 * combate — saíram para "Condições do Personagem" ({@link ./conteudoDeCondicoes}), pedido
 * do Theo do mesmo dia: a ficha numa página, o que acontece com ela noutra.
 */
import { CORES_DAS_LINHAS, FAMILIAS } from './conteudoDeCenasAcoes';
import {
  iconeDeAcao,
  iconeDeAtributo,
  iconeDePericia,
  iconeDePortador,
  iconeDerivado,
  iconeDoMenu,
} from './iconesDeConteudo';
import { col, familiasNaOrdem, montarTabela } from './tabelasEditaveis';
import type { GrupoDeQuadros, SecaoDeQuadros } from './PaginaDeQuadros';
import type { Tabela } from './tabelasEditaveis';

/*
 * Sem checkboxes de entrega, e é decisão (Theo, 19/08/2026): o recorte de temporada
 * desta página são as PRÓPRIAS ABAS — Beta/Season 1 num documento, Season 2+ noutro.
 * Marcar linha a linha por cima disso seria duas réguas para a mesma pergunta.
 */

/**
 * As famílias que pintam as perícias — as MESMAS cinco classes de cor da tela de Cenas
 * (`fam-*` em `tabelaEditavel.css`), com o rótulo que faz sentido aqui.
 *
 * <p>Reusar os ids em vez de criar `fam-ofensiva` etc. foi pedido do Theo ("cores como
 * as usadas na página das cenas") e poupa a folha de estilo de um segundo vocabulário:
 * o vermelho já diz "combate/evento" no produto inteiro, o verde já diz "o que o mundo
 * dá". O id é da folha de estilo; o rótulo é da página.
 */
export const CORES_DAS_PERICIAS: { id: string; rotulo: string }[] = [
  { id: FAMILIAS.evento, rotulo: 'Combate — ofensiva' },
  { id: FAMILIAS.npc, rotulo: 'Combate — defensiva' },
  { id: FAMILIAS.lugar, rotulo: 'Coleta' },
  { id: FAMILIAS.producao, rotulo: 'Criação' },
  { id: FAMILIAS.descanso, rotulo: 'Descanso' },
];

/** No quadro de ações valem os mesmos rótulos da tela de Cenas — é a mesma taxonomia. */
export const CORES_DAS_ACOES = CORES_DAS_LINHAS;

// ------------------------------------------------------------------ atributos

const ATRIBUTOS = montarTabela(
  [
    col('sigla', 'Sigla'),
    col('atributo', 'Atributo'),
    col('governa', 'O que governa', true),
    col('acoes', 'Ações e derivados', true),
  ],
  [
    ['FOR', 'Força', 'Potência bruta.', 'Carga, dano, bloqueio, equipar armas pesadas, testes de esforço físico.'],
    ['DES', 'Destreza', 'Precisão.', 'Acerto de perfurar, disparar e arremessar; forja, couraria, trabalho em madeira; furto, desarmar armadilha.'],
    ['AGI', 'Agilidade', 'Movimento.', 'Iniciativa, esquiva, bloqueio, fuga e perseguição, furtividade, equilíbrio.'],
    ['VIT', 'Vitalidade', 'Resistência.', 'Vigor, regeneração ao descansar e dormir, minerar, viajar sem cansar, resistir a veneno.'],
    ['INT', 'Intelecto', 'Mente.', 'Energia, espaços de memória, magia, criações, resistência mental. Os testes de Vontade são regidos por ele.'],
    ['PER', 'Percepção', 'Leitura do ambiente.', 'Coletar, caçar, iniciativa, acerto de disparo e arremesso, ver armadilha, alquimia.'],
  ]
);

const CUSTO_DE_ATRIBUTO = montarTabela(
  [
    col('nivel', 'Nível'),
    col('degrau', 'Subir para este nível custa'),
    col('total', 'Custo total desde o 0'),
  ],
  [
    ['1', '1', '1'],
    ['2', '2', '3'],
    ['3', '3', '6'],
    ['4', '4', '10'],
    ['5', '5', '15'],
    ['6', '6', '21'],
    ['7', '7', '28'],
    ['8', '8', '36'],
    ['10', '10', '55'],
  ]
);

// ------------------------------------------------- inventário e equipamento

const ENCAIXES = montarTabela(
  [
    col('encaixe', 'Encaixe (design)'),
    col('slot', 'Slot (código)'),
    col('nota', 'Nota', true),
  ],
  [
    ['Cabeça', 'cabeca', ''],
    ['Armadura / Peito', 'peito', ''],
    ['Mão direita', 'mao_direita', 'Arma de duas mãos ocupa as duas mãos e devolve o que estava na outra à mochila — e vice-versa.'],
    ['Mão esquerda', 'mao_esquerda', ''],
    ['Calçado', 'botas', ''],
    ['Ombro / Capa', 'ombros', ''],
    ['Mochila', 'costas', 'Equipamento com bônus de capacidade (carry) e de espaços (slots); desequipar reverte os dois — e é recusado se deixar o inventário acima do limite novo.'],
    ['Cinto', 'cinturao', 'Modular: cada cinto declara quantos espaços de acesso rápido dá. Ver o quadro do cinto.'],
    ['Luvas', 'luvas', ''],
    ['Pescoço', 'pescoco', ''],
    ['Anel / Bracelete', 'anel', ''],
    ['2º Anel / Bracelete', 'anel_2 (proposto)', 'O design pede dois encaixes de anel; o código tem um. Ver Conflitos.'],
    ['—', 'calcas', 'Extra do código, sem correspondente nos doze do design.'],
  ]
);

/** O cinto — Beta/Season 1 por decisão do Theo (19/08/2026), separado dos portadores
 *  de magia, que ficaram com a magia no Season 2+. */
const CINTO = montarTabela(
  [col('regra', 'Regra'), col('como', 'Como funciona', true)],
  [
    [
      'O que é',
      'Um equipamento do encaixe cinturao que declara belt_slots — quantos espaços de acesso rápido dá. Cada cinto tem o seu; um teto global de salvaguarda fica em configuração (combat.belt_slots).',
    ],
    [
      'Em combate',
      'Só existe o que está equipado e no cinto — a mochila fica fechada. Usar item do cinto consome a ação/pontos do turno; não é efeito de graça.',
    ],
    [
      'O que pode pendurar',
      'Itens de acesso rápido: consumíveis entram (poção aceita), armadura de placas é rejeitada. Os portadores de magia (grimório, porta-pergaminhos) entram com a magia, no Season 2+.',
    ],
    [
      'Capacidade',
      'in_belt marca o stack inteiro — uma linha é um estado; belt_slots conta por linha/tipo, não por unidade. Exceder rejeita ("cinto cheio") e o item fica na mochila, fora de alcance em combate.',
    ],
    [
      'Trocar de cinto',
      'Trocar ou desequipar por um de capacidade menor rebaixa automaticamente os itens excedentes para fora do cinto.',
    ],
  ]
);

/** Grimório, pergaminho e memória — Season 2+, junto da magia e das perícias
 *  Memorizar e Escrever. */
const GRIMORIO_E_MEMORIA = montarTabela(
  [
    col('portador', 'Portador'),
    col('guarda', 'O que guarda', true),
    col('regra', 'Regra', true),
  ],
  [
    [
      'Grimório',
      'Até 20 páginas de receitas e magias. Peso 4.',
      'Só libera as magias nas ações de combate se estiver pendurado no cinto — na mochila não libera nada.',
    ],
    [
      'Porta-Pergaminhos',
      'Até 5 pergaminhos. Peso 2.',
      'Mesma regra do grimório: no cinto libera, na mochila não.',
    ],
    [
      'Pergaminho',
      'Uma receita. Sozinho quase não pesa.',
      'É o papel do Escrever e do Estudar — de onde a receita vai para a memória ou para o grimório.',
    ],
    [
      'Memória',
      'Espaços mentais = Intelecto + nível da perícia Memorizar.',
      'Dispensa o item físico: magia memorizada vale em combate. Memorizar num espaço cheio apaga o que estava lá.',
    ],
  ]
);

// ------------------------------------------------------------------ consumo

const CONSUMO = montarTabela(
  [
    col('efeito', 'Efeito'),
    col('faz', 'O que faz', true),
    col('exemplo', 'Exemplo', true),
  ],
  [
    ['heal', 'Cura Vigor, travando no teto.', 'Vigor 92/100 + poção de 15 → 100, não 107.'],
    ['restore_mp', 'Restaura Energia, travando no teto.', 'Intelecto 4 e perícia 0 → teto 17; 15/17 + 5 → 17.'],
    [
      'apply_status',
      'Buff temporário por N turnos; expira exatamente no turno marcado. Re-beber o mesmo buff renova a duração e NÃO soma o valor.',
      'Mbuna Temperada: +2 Vitalidade por 8 turnos. Beber duas continua +2.',
    ],
    ['clear_condition', 'Limpa uma condição persistente.', 'O caso desenhado é a comida limpar a FOME — que ficou para o Season 2+; o efeito nasce pronto para ela.'],
    [
      'regen',
      'Regenera por turno durante a digestão — é o que separa comida (cura ao longo do caminho) de poção (spike).',
      'Mbuna: +4 Vigor por turno durante os 5 turnos de digestão.',
    ],
    [
      'food.digest_turns',
      'Inicia a digestão: comer de novo antes do fim é recusado, informando quantos turnos faltam. Conta em turnos de mundo.',
      'Fruta digere em 1–2 turnos, prato cozido em ~3, carne em 4–6. Mangaba 1 · Caldo Amarelo 3 · Mbuna 5.',
    ],
  ]
);

// ----------------------------------------------------------------- perícias

const PERICIAS = montarTabela(
  [
    col('pericia', 'Perícia'),
    col('atributos', 'Atributos do teste'),
    col('sobe', 'Como sobe'),
    col('nota', 'Observação', true),
  ],
  [
    // Combate — ofensivas
    ['Desarmado', 'chave da arma ×2 + suporte', 'Uso em combate — máx. 1 XP por combate', 'Punho, cotovelo, joelho. Nunca se está sem ela.'],
    ['Espada', 'DES ×2 + FOR', 'Uso em combate — máx. 1 XP por combate', 'Corte e estocada: o equilíbrio entre alcance e cadência.'],
    ['Machado', '', 'Uso em combate — máx. 1 XP por combate', 'Peso que abre guarda. Lento, e caro de errar.'],
    ['Haste', '', 'Uso em combate — máx. 1 XP por combate', 'Lança e alabarda: acerta antes de ser alcançado.'],
    ['Contusão', '', 'Uso em combate — máx. 1 XP por combate', 'Impacto que ignora armadura em vez de perfurá-la.'],
    ['Arco', '', 'Uso em combate — máx. 1 XP por combate', 'Distância e silêncio. Pede espaço e mãos livres.'],
    ['Armas Exóticas', '', 'Uso em combate — máx. 1 XP por combate', 'Só no épico-05 e no GDD — a Ficha a aposentou em favor da Magia Ofensiva (aba Season 2+). A decidir.'],
    // Combate — defensivas
    ['Esquiva', 'AGI', 'Reagir em combate — máx. 1 XP por combate', 'Não estar onde o golpe chega. Há também uma esquiva base estática na ficha, sempre pior que reagir.'],
    ['Bloqueio', 'FOR + AGI', 'Reagir em combate — máx. 1 XP por combate', 'Estar, e aguentar: reduz mais quanto maior a margem de vitória.'],
    // Coleta
    ['Herbalismo', 'PER + VIT', 'Cada extração — sem cap fora de combate', 'Do que cresce: ervas, fungos, flores. Alimenta a Alquimia.'],
    ['Mineração', 'VIT', 'Cada extração — sem cap fora de combate', 'Da pedra: minério e gema. Exige ferramenta na mão. Alimenta a Forja.'],
    ['Esfolamento', '', 'Cada extração — sem cap fora de combate', 'Do que foi abatido: couro, escama, osso. Alimenta a Couraria.'],
    ['Caçar', 'PER ×2', 'Cada caçada — sem cap fora de combate', 'Citada nos testes e fora das quatro famílias do GDD (inconsistência #3). A decidir.'],
    // Criação
    ['Alquimia', 'INT + PER', 'Cada tentativa, com ou sem sucesso', 'Poções, venenos e elixires. Falha consome material; o resultado afeta também a quantidade produzida.'],
    ['Forja', 'DES + PER', 'Cada tentativa, com ou sem sucesso', 'Armas, ferramentas e armadura pesada. Dá para pagar a forja de um NPC.'],
    ['Couraria', 'DES', 'Cada tentativa, com ou sem sucesso', 'Armadura leve, bolsas e acessórios.'],
    ['Trabalho em Madeira', '', 'Cada tentativa, com ou sem sucesso', 'Sem mecânica escrita no GDD — hoje não tem como subir.'],
    ['Joalheria', '', 'Cada tentativa, com ou sem sucesso', 'Sem mecânica escrita no GDD — hoje não tem como subir.'],
    // Descanso
    ['Medicina', '', 'Subação Medicar, ao descansar', 'Gasta insumo, devolve mais Vigor que o cochilo e estanca sangramento.'],
    ['Cozinhar', '', 'Subação Cozinhar, ao descansar', 'Sem mecânica de subida escrita no GDD. A comida que produz (digestão, buff) entra no Season 2+.'],
    ['Acampar', '', 'Dormir em acampamento', 'O único degrau da escala da dormida que cresce com perícia.'],
  ]
);

const FAMILIAS_DAS_PERICIAS = familiasNaOrdem(
  // ofensivas
  FAMILIAS.evento,
  FAMILIAS.evento,
  FAMILIAS.evento,
  FAMILIAS.evento,
  FAMILIAS.evento,
  FAMILIAS.evento,
  FAMILIAS.evento,
  // defensivas
  FAMILIAS.npc,
  FAMILIAS.npc,
  // coleta
  FAMILIAS.lugar,
  FAMILIAS.lugar,
  FAMILIAS.lugar,
  FAMILIAS.lugar,
  // criação
  FAMILIAS.producao,
  FAMILIAS.producao,
  FAMILIAS.producao,
  FAMILIAS.producao,
  FAMILIAS.producao,
  // descanso
  FAMILIAS.descanso,
  FAMILIAS.descanso,
  FAMILIAS.descanso
);

/**
 * As perícias que ficaram para depois da Season 1 — o quadro da ABA Season 2+.
 *
 * <p>São as quatro que o Theo marcou como `depoisDaSeason1` no catálogo
 * (`pericias.ts`): elas aparecem em cinza na Ficha e na Tier List, e aqui ganham a
 * própria mesa. A mesa do Mistik Dream as usa mesmo assim — é para isso que uma mesa
 * experimental serve.
 */
const PERICIAS_S2 = montarTabela(
  [
    col('pericia', 'Perícia'),
    col('atributos', 'Atributos do teste'),
    col('sobe', 'Como sobe'),
    col('nota', 'Observação', true),
  ],
  [
    ['Besta', '', 'Uso em combate — máx. 1 XP por combate', 'Força emprestada: mira fácil, recarga lenta.'],
    ['Magia Ofensiva', '', 'Uso em combate — máx. 1 XP por combate', 'Absorve o "Combater com Magia" do épico-05 e do GDD. O dano que não vem do braço.'],
    ['Magia Defensiva', '', 'Por posse (destrava a ação)', 'Absorve o "Escudo Arcano" do épico-05 — decisão do Theo, 19/08/2026. A magia que apara: selo, barreira, dissipação.'],
    ['Tecnomagia', '', 'Cada tentativa, com ou sem sucesso', 'Metal e feitiço no mesmo objeto. Sem coleta própria — a primeira criação que não nasce de um par.'],
    ['Memorizar', 'INT', 'Subação Estudar, ao descansar', 'Cada nível dá +1 espaço de memória — a perícia que aumenta a própria memória. Entra junto com o grimório e a memória.'],
    ['Escrever', 'INT + DES', 'Subação Escrever, ao descansar', 'Transfere receita para pergaminho ou grimório. Exige ferramenta de escrita com tinta consumível.'],
  ]
);

const FAMILIAS_DAS_PERICIAS_S2 = familiasNaOrdem(
  FAMILIAS.evento,
  FAMILIAS.evento,
  FAMILIAS.npc,
  FAMILIAS.producao,
  FAMILIAS.descanso,
  FAMILIAS.descanso
);

/** O backlog do personagem: o que já foi desenhado, por que saiu, e o que traz de volta. */
const APOSTAS_S2 = montarTabela(
  [
    col('assunto', 'Assunto'),
    col('definido', 'O que já está definido', true),
    col('gatilho', 'O que traz de volta / faz entrar', true),
    col('fonte', 'Fonte'),
  ],
  [
    [
      'Magia (o sistema)',
      'Sistema inteiro em aberto: as perícias de magia existem, mas o que elas rolam não tem regra escrita.',
      'Escrever o sistema antes de qualquer conteúdo de magia virar seed.',
      'GDD v3.3 (Em aberto)',
    ],
    [
      'Sono',
      'Condição acumulativa da parte final do dia: penalidade crescente a todos os testes, reduzida ao descansar, zerada ao dormir. Removida na v3.1 — a Energia e o esgotado ocupam o lugar (decisão registrada nos Conflitos da aba Beta/Season 1).',
      'Volta se o teste do Beta mostrar jogadores virando a madrugada sem consequência sentida.',
      'GDD v3.3 (backlog)',
    ],
    [
      'Teste de Viajar',
      'Viajar roda um teste de Vitalidade nos bastidores: falhar dá +1 Cansaço (a carga piora a chance) e a tentativa rende XP.',
      'Entra no Season 2+ (decisão do Theo, 19/08/2026) — a ação Viajar continua no Beta, sem o teste. Atenção: ele é o único gatilho do Cansaço.',
      'GDD v3.3 · épico-03',
    ],
    [
      'Respec de build',
      'Não no Beta (a armadilha do Carisma já foi mitigada escondendo-o). Em S1, recompensa rara de quest; em S2, avaliar respec pago SEM vantagem permanente (anti-P2W).',
      'Decisão de roadmap do produto.',
      'épico-03',
    ],
    [
      'Vitrine cosmética',
      'Separar a aparência (skin) do bônus no equip_data, para a vitrine reaproveitar o paper-doll sem mexer no balanço. Não modelar bônus e visual no mesmo campo.',
      'Monetização S1+.',
      'épico-04',
    ],
    [
      'Durabilidade e quebra de itens',
      'Não planejada para o Beta — registrada como ponto de extensão (equip_data.durability). As ferramentas já nascem com a regra "quanto melhor, mais frágil".',
      'Detalhar a regra de desgaste e reposição.',
      'épico-04 · GDD (pendência)',
    ],
    [
      'Casa própria e sede de guilda',
      'Estavam no topo da escala da dormida antiga e não aparecem na nova escala de cinco degraus.',
      'Rever quando a escala da dormida for fechada.',
      'GDD v3.3 (pendência)',
    ],
    [
      'Clima e reputação regional',
      'Ideias registradas sem desenvolvimento.',
      'Sem gatilho definido.',
      'GDD v3.3 (backlog)',
    ],
  ]
);

// -------------------------------------------------------------------- teste

const TESTE = montarTabela(
  [
    col('acao', 'Ação'),
    col('primario', 'Atributo primário (×2)'),
    col('secundario', 'Secundário'),
  ],
  [
    ['Ataque físico', 'o da arma equipada', 'suporte da arma + equipamento'],
    ['Caçar', 'Percepção', '—'],
    ['Coletar', 'Percepção', 'Vitalidade'],
    ['Minerar', 'Vitalidade', '—'],
    ['Alquimia', 'Intelecto', 'Percepção'],
    ['Forjar', 'Destreza', 'Percepção'],
    ['Couraria', 'Destreza', '—'],
  ]
);

// --------------------------------------------------------------- progressão

const PROGRESSAO = montarTabela(
  [
    col('nivel', 'Nível da perícia'),
    col('xp', 'XP acumulado'),
    col('combates', 'Combates distintos (cap 1/combate)'),
  ],
  [
    ['1', '1', '1'],
    ['2', '3', '3'],
    ['3', '6', '6'],
    ['4', '10', '10'],
    ['5', '15', '15'],
    ['10', '55', '55'],
  ]
);

// ---------------------------------------------------------------- reputação

const REPUTACAO = montarTabela(
  [col('valor', 'Valor acumulado'), col('faixa', 'Faixa')],
  [
    ['−51 ou menos', 'Hostil'],
    ['−50 a −1', 'Desconfiado'],
    ['0 a 24', 'Neutro'],
    ['25 a 49', 'Amigável'],
    ['50 a 74', 'Aliado'],
    ['75 ou mais', 'Devoto'],
  ]
);

// -------------------------------------------------------------------- ações

const ACOES = montarTabela(
  [
    col('acao', 'Ação'),
    col('turno', 'Turno'),
    col('custo', 'Custo / risco'),
    col('efeito', 'O que faz no personagem', true),
  ],
  [
    [
      'Viajar',
      '1',
      'Energia',
      'Leva a uma área adjacente; chegando, consulta a tabela de encontros do destino. O teste de Vitalidade dos bastidores (Cansaço, XP) entra no Season 2+.',
    ],
    [
      'Explorar',
      '1',
      'Energia',
      'Única por área. Depois dela a área libera caçar, coletar madeira, forragear e o acesso a minas e locais de inimigos. Não produz nada no dia em que é feita.',
    ],
    [
      'Coletar · Caçar · Minerar',
      '1',
      'Chance de encontro',
      'Teste com faixa de rendimento própria da área. Minerar exige a ferramenta na mão; as tabelas de caça mudam com a faixa do dia.',
    ],
    [
      'Criar (alquimia, forja, couraria)',
      '1',
      'Energia + materiais, mesmo na falha',
      'Exige as três ao mesmo tempo: ferramenta certa, receita (memória ou papel) e materiais — faltando qualquer uma, a ação nem aparece. Resultado vai de falha a obra-prima.',
    ],
    [
      'Descansar',
      '1',
      'Chance de encontro — parar não é ficar seguro',
      'Abre as subações (Cochilar, Medicar, Cozinhar; Estudar e Escrever no Season 2+). O que cada uma devolve está em "Condições do Personagem", no quadro do descanso.',
    ],
    [
      'Dormir',
      '4 fixos',
      'Sempre pede confirmação',
      'Encerra o dia do personagem e do jogador; a energia de amanhã vem da escala da dormida, em "Condições do Personagem". Dormir cedo desperdiça (acorda no 1º turno mesmo assim); virar a madrugada rouba turnos do amanhã.',
    ],
    [
      'Usar item · conversar · negociar · interagir · deslocar na área',
      '0',
      '—',
      'Não gastam turno.',
    ],
    [
      'Combater',
      '0',
      'Energia por ação',
      'Quem pagou o turno foi a ação que levou até ali. Em combate só existe o que está equipado e no cinto; iniciativa e esquiva leem a Agilidade já penalizada pela carga.',
    ],
  ]
);

const FAMILIAS_DAS_ACOES = familiasNaOrdem(
  FAMILIAS.lugar, // viajar
  FAMILIAS.lugar, // explorar
  FAMILIAS.producao, // coletar/caçar/minerar
  FAMILIAS.producao, // criar
  FAMILIAS.descanso, // descansar
  FAMILIAS.descanso, // dormir
  FAMILIAS.npc, // usar item / conversar
  FAMILIAS.evento // combater
);

// ---------------------------------------------------------------- conflitos

/**
 * Onde as fontes dizem coisas diferentes sobre o personagem.
 *
 * <p>As cinco primeiras linhas já têm decisão — quatro do Theo em 19/08/2026, uma do
 * CEO no épico-03 — e ficam aqui como registro, não como pergunta. Nas demais a coluna
 * nasce vazia de propósito: um chute pré-preenchido se lê como decisão tomada, e o time
 * passaria a discutir o que a tela inventou.
 */
const CONFLITOS = montarTabela(
  [
    col('assunto', 'Assunto'),
    col('epicos', 'O que os épicos dizem', true),
    col('gdd', 'O que o GDD v3.3 diz', true),
    col('codigo', 'O que o código faz', true),
    col('decisao', 'Decisão do Theo', true),
  ],
  [
    [
      'Fórmula da carga',
      'Divergem entre si: o épico-03 manda seguir a fonte (X = 2 + Força×3); o épico-04 manda manter o código (+ Vitalidade×1).',
      '"Vem de Força" — número no documento de balanceamento.',
      'X = 2 + Força×3 + Vitalidade×1 + bônus de mochila.',
      '19/08/2026: vale a do código, com Vitalidade. Atualizar a fonte de design.',
    ],
    [
      'Sono',
      'Épico-03 mantém no Beta: turno ≥ 9 dá +1 Sono, flat −1 por nível, dormir limpa.',
      'Removido na v3.1 — a Energia e o estado esgotado ocupam o lugar. Volta do backlog se o teste mostrar madrugada sem consequência.',
      'Não existe.',
      '19/08/2026: fora da lista de condições — fica no backlog, como no GDD.',
    ],
    [
      'Energia como custo de ação',
      'SP só em combate (default 0), fórmula de teto 5 + INT×3 + perícia.',
      'Canônico e estrutural: toda ação custa energia; combater gasta energia e não turno; negativa = esgotado.',
      'Não implementada fora do combate.',
      '19/08/2026: vale o modelo do GDD; a fórmula de teto dos épicos fica registrada.',
    ],
    [
      'Carisma',
      'Fora dos 6 — Vontade é teste de Intelecto. Remover exige story de limpeza.',
      'Seis atributos, sem Carisma.',
      '7ª chave charisma (migration V34) que nenhuma regra lê.',
      'CEO: fora do Beta — sem efeito, oculto na criação; reavaliar em S1. Falta a story de limpeza.',
    ],
    [
      'Escudo Arcano × Magia Defensiva',
      'Épico-05 lista "Escudo Arcano" como perícia própria, destravada por posse.',
      'Idem, na família Combate.',
      'skill_id escudo_arcano existe como unlock de ação.',
      '19/08/2026: Magia Defensiva absorve o Escudo Arcano; Magia Ofensiva absorve o "Combater com Magia". Sem duplicar.',
    ],
    [
      'Peso conta os equipados?',
      'Sim — "todos os itens equipados e na mochila"; sem isso a penalidade se burla vestindo tudo.',
      '—',
      'Conta só a mochila (o item equipado sai do inventário).',
      '',
    ],
    [
      'Requisito de equipar: base ou efetivo?',
      'Efetivo — um anel de +2 Força habilita a peça que exige Força 10.',
      '"Usar equipamentos mais pesados" sob Força sugere efetivo.',
      'Confere o atributo base.',
      '',
    ],
    [
      'Conjunto final de slots',
      'Adicionar o 2º anel (anel_2); mochila é o slot costas; manter calcas.',
      'Doze encaixes, com dois de anel/bracelete e a mochila como encaixe.',
      'Um anel só; ombros e costas separados; calcas extra.',
      '',
    ],
    [
      'Cinto',
      'Container modular: belt_slots por item de cinto, itens marcados in_belt, o stack inteiro de uma vez.',
      'Modular, com encaixes extras de acesso rápido. Quantidade não consta.',
      'Um slot único.',
      '',
    ],
    [
      'Faixas de carga',
      'Cinco faixas com −AGI progressiva, bloqueio só acima de 5X, breakdown visível na iniciativa.',
      'Cinco faixas, de super leve a máxima, um degrau de AGI cada.',
      'Bloqueio binário em 1X, sem faixas nem penalidade.',
      '',
    ],
    [
      'Dado e críticos',
      '1d12; 1 → −5, 12 → +5; o 12 é sucesso garantido (válvula contra a espiral de condições).',
      'd12; o mínimo derruba e o máximo eleva, sem cravar valor. Crítico de ataque é outro sistema: multiplica dano (inconsistência #5).',
      'DiceRoller roda d10 e não trata crítico. A mesa (Ficha) rascunha com dois atributos + d20.',
      '',
    ],
    [
      'Turnos do dia',
      '8 — o motor roda com turn.per_day = 8.',
      '12, do nascer do sol à madrugada; a matemática de dormir só fecha com 12.',
      '8.',
      'Também está na tela de Cenas — decidir lá vale aqui.',
    ],
    [
      'XP de perícia: pool gasto ou marco?',
      'Pool gasto: subir consome o saldo (custo = nível+1); level-up manual no botão.',
      '"Da mesma forma que os atributos" — leitura literal seria marco acumulado.',
      'Não implementado.',
      '',
    ],
    [
      'Cap de XP fora de combate',
      'Sem cap — o custo de turno e recurso já limita o farm.',
      'Teto por período "ainda não existe" — risco de snowball registrado.',
      '—',
      '',
    ],
    [
      'Perícias fora das famílias',
      'Pendentes: Caçar, Coletar, Fuga, Perseguição, Vigor, Energia — mapear (forragear → Herbalismo?) ou cortar.',
      'Inconsistência #3 (Alta): os testes usam perícias que nenhuma família lista.',
      'skill_id é texto livre.',
      '',
    ],
    [
      'Faixas de reputação',
      'Mover o cálculo do cliente para o servidor, limiares em game_config; a assimetria 4 positivas × 2 negativas é intencional?',
      'Reputação não existe como sistema — só "clima e reputação regional" no backlog.',
      'Motor pronto (delta_rep, gates); a faixa é calculada no front.',
      '19/08/2026: reputação movida para o Season 2+ — o resto se decide quando ela entrar.',
    ],
    [
      'Vigor na ficha',
      'Criar vigorCurrent/vigorMax com a fórmula, e renomear mana* → energy* na mesma migration.',
      'Vigor é canônico; vale a fórmula das Mecânicas Místikas (número no balanceamento).',
      'Só existe mana* (SP). Não há campo de HP.',
      '',
    ],
    [
      'Auto-dormir do Cansado 5',
      'Só limpa o Cansaço e consome os turnos restantes — a virada completa fica para o Dormir voluntário.',
      '"O último nível faz dormir contra a vontade" — sem dizer o que acontece com o dia.',
      '—',
      '',
    ],
    [
      'Fome num salto de dias',
      'advance_day by:2 — a Fome sobe no máximo 1 por virada, ou N pelos dias pulados?',
      'Um nível por dia sem comer.',
      '—',
      '19/08/2026: a Fome inteira foi movida para o Season 2+ — decidir junto com a entrada dela.',
    ],
    [
      'Piso do atributo efetivo',
      'Piso 0 por atributo (a soma bruta fica auditável); o total do teste ainda pode ser negativo.',
      '—',
      'Não há atributo efetivo fora de combate.',
      '',
    ],
    [
      'Escala da dormida × penalidade antiga',
      '—',
      'A escala anterior punia os testes do dia seguinte no relento e no acampamento — substituída ou coexistem? Casa própria e sede de guilda sumiram do topo.',
      '—',
      '',
    ],
  ]
);

// ---------------------------------------------------------- prosa e avisos

/**
 * A prosa e o aviso de fábrica de cada quadro — o que a tela mostra antes de o time
 * escrever por cima. Mesma regra da tela de Cenas: texto puro, sem markdown.
 */
const PROSA: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  atributos: {
    texto:
      'Seis, e só seis: começam em 0, não têm teto (o teto 5 vale só na criação), e o jogador distribui 36 pontos ao criar o personagem. O que sobra fica guardado e pode ser gasto a qualquer momento pela tela de atributos; pontos novos vêm de subir de nível. Vontade não é atributo — é um teste regido por Intelecto.',
    alerta:
      'O código tem uma 7ª chave, charisma (migration V34), que nenhuma regra lê. Decisão do CEO: Carisma fora do Beta — oculto na criação, sem efeito; reavaliar em S1. Falta a story de limpeza (tirar de VALID_ATTRIBUTES e migrar o JSON).',
  },
  'custo-de-atributo': {
    texto:
      'Custo triangular: subir do nível N para N+1 custa N+1 pontos; o total até o nível K é K(K+1)/2. A curva pune a profundidade extrema de propósito — Força 8 consome os 36 pontos inteiros, enquanto a build equilibrada 3/3/3/3 custa 24 e guarda 12. E como as condições de sobrevivência mordem o próprio atributo (a Fome, quando entrar no Season 2+, ataca a Força), o "tudo num só" é também o mais frágil. O gasto pós-criação recomputa o custo no servidor; não há rebaixar para reembolsar, e respec não é escopo do Beta. O mesmo triangular vale para o level-up de perícia.',
  },
  'inventario-e-encaixes': {
    texto:
      'A mochila é uma lista com dois limites: o peso (X, do quadro de carga) e 20 espaços (inventory.max_slots). Itens iguais empilham numa linha só, e empilhar não consome espaço novo. Nunca dois itens no mesmo encaixe: equipar devolve o anterior à mochila — e se a mochila não comporta a devolução, a troca é recusada e nada muda. Os bônus dos encaixes somam (hp, atk, armor, carry, slots e qualquer atributo) e refletem no atributo efetivo. Requisitos de equipar: nível e atributo mínimo, com a recusa dizendo quanto falta ("Força 8/10 — faltam 2").',
  },
  cinto: {
    texto:
      'A regra central do combate mora aqui: o cinto é o que separa o que está à mão do que está guardado — Beta/Season 1 por decisão do Theo (19/08/2026). Os portadores de magia (grimório, porta-pergaminhos, memória) ficaram com a magia, na aba Season 2+.',
    alerta:
      'Hoje o código tem um slot único de cinto; o modelo modular (belt_slots por item, in_belt por linha) é implementação nova — D8 do épico-04.',
  },
  pericias: {
    texto:
      'A união das três fontes, sem duplicar — decisão do Theo (19/08/2026): as da tela Ficha, mais as que só o épico-05 e o GDD trazem. As de magia, a Besta, a Tecnomagia, o Memorizar e o Escrever ficaram para depois — moram na aba Season 2+. Toda perícia começa em 0: nível 0 é travada, nível 1 ou mais é possuída. Ela entra ×1 na fórmula do teste — nível 4 soma 4, não 8. Uma ação pode exigir a posse (sem ela, some da barra) ou um nível mínimo ("Estocada Veloz exige Espada 3"). As cores agrupam como na tela de Cenas.',
    alerta:
      'Fora das famílias e citadas nos testes (inconsistência #3 do GDD, prioridade Alta): Coletar, Fuga, Perseguição, Vigor e Energia — decidir se viram perícia, se mapeiam (forragear → Herbalismo?) ou se saem. E no Beta só o grupo de Combate tem fonte de XP: os demais devem exibir "Em breve" para não parecer bug.',
  },
  teste: {
    texto:
      'Toda ação que pode falhar rola: 1d12 + atributo primário ×2 + secundários + nível da perícia + equipamento ou ferramenta + bônus de situação − penalidades ativas. O resultado compara contra o oponente, a receita ou a dificuldade declarada (CD 0 = sucesso garantido; 999 = falha garantida). Crítico: 1 → −5 e 12 → +5; o 12 é sucesso garantido — a válvula contra a espiral de condições — e o 1 é falha, sem empilhar o −5. A penalidade flat soma ao total e só pesa nos rolls não-críticos. Três alavancas para o mesmo teste: atributo é caro e lento, perícia sobe com uso, ferramenta se compra e quebra. Os testes de Escrever, Estudar e Viajar ficaram para o Season 2+ — ver a outra aba.',
    alerta:
      'O motor de hoje rola d10 e não trata os críticos. A mesa (tela Ficha) rascunha com dois atributos + d20. E o crítico do teste convive com o crítico de ataque, que multiplica dano — mesmo nome, sistemas diferentes (inconsistência #5 do GDD): renomear um ou unificar.',
  },
  progressao: {
    texto:
      'O laço: usar → ganhar XP → subir → ficar melhor. +1 XP na perícia a cada ação que exige teste — a concessão acontece na rolagem, então auto-sucesso sem dado não dá XP. O ataque físico treina a perícia da arma equipada. Cap: 1 XP por perícia por combate (combates diferentes acumulam; várias perícias no mesmo combate ganham 1 cada); fora de combate não há cap — o custo de turno já limita. Subir é manual, no botão, modelo pool gasto: o próximo nível custa nível+1 e debita o saldo; sem saldo, recusa sem perder nada. O fim do combate anuncia: "Você melhorou: Espada +1, Esquiva +1". Fuga de encontro gerado por descanso não concede XP.',
    alerta:
      'Nível de PERSONAGEM está em aberto no GDD: aparece em três fórmulas (Vigor, Energia, requisito de ação) e não tem regra de progressão escrita. E o reset é por temporada — o personagem começa do zero; o que persiste é a lore e a reputação do jogador na comunidade.',
  },
  acoes: {
    texto:
      'Um recorte de propósito: só o que mexe na ficha. O catálogo completo de tipos de cena e ações — e os conflitos dele, como os 8 × 12 turnos do dia — mora na tela "Cenas, Ações e Fluxo de Jogo". O dia tem doze turnos no GDD (o motor roda com oito), e as faixas do dia dão vocação a cada hora: caça diurna de manhã, coletas exclusivas à noite, risco máximo na madrugada.',
  },
  conflitos: {
    texto:
      'Onde as fontes dizem coisas diferentes sobre o personagem. As cinco primeiras linhas já têm decisão — quatro do Theo em 19/08/2026, uma do CEO no épico-03 — e ficam como registro. Nas demais, a coluna do Theo nasce vazia de propósito: um chute pré-preenchido se lê como decisão tomada.',
  },
};

/** A prosa da aba Season 2+ — mesma regra: texto puro, o que o time gravar vence. */
const PROSA_S2: Record<string, Pick<Tabela, 'texto' | 'alerta'>> = {
  'consumo-e-comida': {
    texto:
      'Acompanha a Fome — decisão do Theo (19/08/2026). A ação Comer entra junto: zera a Fome inteira e inicia a digestão (nova comida só depois; o prato regenera ou dá buff enquanto digere; comer sem fome é no-op). Consumir remove 1 unidade do item; duas requisições do mesmo uso consomem uma só. Efeitos e cena combinam: um item pode curar E levar a uma cena. A digestão conta em turnos de mundo, e o prato deve compensar os turnos presos digerindo (digestão 3 → buff de 6–8 turnos). A subação Cozinhar já existe no Beta — o que fica para cá é a comida fazer efeito.',
  },
  'grimorio-e-memoria': {
    texto:
      'Receitas e magias vivem em três lugares — na memória, no pergaminho e no grimório — e entram com a magia, junto das perícias Memorizar e Escrever. Os portadores só liberam magia em combate quando pendurados no cinto (que já existe no Beta).',
  },
  reputacao: {
    texto:
      'Movida para o Season 2+ (decisão do Theo, 19/08/2026). Toda relação social é um número em duas dimensões independentes — por NPC e por facção —, sem auto-propagação: cada delta_rep é explícito e aceita vários alvos numa ação só (+10 num NPC, −10 noutro: escolher lado tem custo). Os gates rep:npc e rep:faction aceitam GTE, LTE, GT, LT, EQ, NEQ e RANGE, e são reavaliados a cada render — ofender volta a trancar a porta que estava aberta. Só relações já tocadas aparecem no painel, e um gate fechado deve expor o motivo ("Requer a confiança do Decano") em vez de a ação sumir.',
    alerta:
      'O GDD v3.3 não traz reputação como sistema — só "clima e reputação regional" no backlog. O motor, porém, está pronto e testado no código; quando entrar, falta mover as faixas do cliente para o servidor (limiares em game_config) e decidir se a assimetria — quatro faixas positivas, duas negativas — é intencional.',
  },
  'pericias-s2': {
    texto:
      'As quatro perícias que o Theo marcou para depois da Season 1. Nas telas de Ficha e Tier List elas aparecem em cinza e recusam edição; a mesa do Mistik Dream as usa mesmo assim — é para isso que uma mesa experimental serve. Quando entrarem, voltam para a tabela da aba Beta/Season 1.',
    alerta:
      'Magia é sistema inteiro em aberto no GDD: as perícias existem, as regras do que elas rolam não. Nada de conteúdo de magia vira seed antes de o sistema ser escrito.',
  },
  'apostas-s2': {
    texto:
      'O backlog do personagem: o que já foi desenhado e ficou para depois, por quê, e o que traz cada coisa de volta. O que tem decisão tomada continua registrado nos Conflitos da aba Beta/Season 1 — aqui mora o que ainda vai ser jogado para frente.',
  },
};

// ------------------------------------------------------------------ o mapa

const QUADROS: Record<string, Tabela> = {
  atributos: ATRIBUTOS,
  'custo-de-atributo': CUSTO_DE_ATRIBUTO,
  'inventario-e-encaixes': ENCAIXES,
  cinto: CINTO,
  pericias: PERICIAS,
  teste: TESTE,
  progressao: PROGRESSAO,
  acoes: ACOES,
  conflitos: CONFLITOS,
};

const FAMILIAS_DAS_LINHAS: Record<string, Record<string, string>> = {
  pericias: FAMILIAS_DAS_PERICIAS,
  acoes: FAMILIAS_DAS_ACOES,
};

/** A aba Beta/Season 1 — a chave `personagem-resumo` no servidor. */
export const TABELAS: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS).map(([chave, quadro]) => [
    chave,
    { ...PROSA[chave], familias: FAMILIAS_DAS_LINHAS[chave], ...quadro },
  ])
);

// ------------------------------------------------------------ a aba Season 2+

const QUADROS_S2: Record<string, Tabela> = {
  'pericias-s2': PERICIAS_S2,
  'consumo-e-comida': CONSUMO,
  'grimorio-e-memoria': GRIMORIO_E_MEMORIA,
  reputacao: REPUTACAO,
  'apostas-s2': APOSTAS_S2,
};

const FAMILIAS_DAS_LINHAS_S2: Record<string, Record<string, string>> = {
  'pericias-s2': FAMILIAS_DAS_PERICIAS_S2,
};

/** A aba Season 2+ — a chave `personagem-season2` no servidor, gravada à parte. */
export const TABELAS_S2: Record<string, Tabela> = Object.fromEntries(
  Object.entries(QUADROS_S2).map(([chave, quadro]) => [
    chave,
    { ...PROSA_S2[chave], familias: FAMILIAS_DAS_LINHAS_S2[chave], ...quadro },
  ])
);

const SECOES_S2_BASE: Record<string, SecaoDeQuadros> = {
  'pericias-s2': {
    titulo: 'As perícias que entram depois',
    rotuloDoItem: 'perícia',
    cores: CORES_DAS_PERICIAS,
  },
  'consumo-e-comida': { titulo: 'Consumo, comida e digestão', rotuloDoItem: 'efeito' },
  'grimorio-e-memoria': { titulo: 'Grimório, pergaminho e memória', rotuloDoItem: 'portador' },
  reputacao: { titulo: 'Reputação — NPC e facção', rotuloDoItem: 'faixa' },
  'apostas-s2': { titulo: 'Backlog e apostas', rotuloDoItem: 'aposta' },
};

const ICONES_S2: Record<string, Pick<SecaoDeQuadros, 'icone' | 'iconeDaLinha'>> = {
  'pericias-s2': {
    icone: iconeDoMenu('espada'),
    iconeDaLinha: (l) => iconeDePericia(l.valores.pericia ?? ''),
  },
  'consumo-e-comida': { icone: iconeDoMenu('pocao') },
  'grimorio-e-memoria': {
    icone: iconeDoMenu('documento'),
    iconeDaLinha: (l) => iconeDePortador(l.valores.portador ?? ''),
  },
  reputacao: { icone: iconeDoMenu('estandarte') },
  'apostas-s2': { icone: iconeDoMenu('quadro') },
};

export const SECOES_S2: Record<string, SecaoDeQuadros> = Object.fromEntries(
  Object.entries(SECOES_S2_BASE).map(([chave, s]) => [chave, { ...s, ...ICONES_S2[chave] }])
);

export const GRUPOS_S2: GrupoDeQuadros[] = [
  { id: 'pericias', titulo: 'Perícias', quadros: ['pericias-s2'] },
  { id: 'itens', titulo: 'Consumo e portadores', quadros: ['consumo-e-comida', 'grimorio-e-memoria'] },
  { id: 'social', titulo: 'Reputação', quadros: ['reputacao'] },
  { id: 'backlog', titulo: 'Backlog', quadros: ['apostas-s2'] },
];

const SECOES_BASE: Record<string, SecaoDeQuadros> = {
  atributos: { titulo: 'Os seis atributos', rotuloDoItem: 'atributo' },
  'custo-de-atributo': { titulo: 'O custo de melhoria', rotuloDoItem: 'nível' },
  'inventario-e-encaixes': {
    titulo: 'Inventário e os doze encaixes',
    rotuloDoItem: 'encaixe',
  },
  cinto: { titulo: 'O cinto', rotuloDoItem: 'regra' },
  pericias: { titulo: 'As perícias', rotuloDoItem: 'perícia', cores: CORES_DAS_PERICIAS },
  teste: { titulo: 'O teste — 1d12', rotuloDoItem: 'ação' },
  progressao: { titulo: 'Progressão e XP de perícia', rotuloDoItem: 'nível' },
  acoes: {
    titulo: 'As ações que tocam o personagem',
    rotuloDoItem: 'ação',
    cores: CORES_DAS_ACOES,
  },
  conflitos: { titulo: 'Conflitos e decisões', rotuloDoItem: 'conflito', destaque: true },
};

/** Os ícones de seção e de linha da aba Beta/Season 1 — ver {@code iconesDeConteudo}. */
const ICONES: Record<string, Pick<SecaoDeQuadros, 'icone' | 'iconeDaLinha'>> = {
  atributos: {
    icone: iconeDoMenu('personagem'),
    iconeDaLinha: (l) => iconeDeAtributo(l.valores.sigla ?? ''),
  },
  'custo-de-atributo': { icone: iconeDerivado('custo-do-ponto') },
  'inventario-e-encaixes': {
    icone: iconeDoMenu('bau'),
    iconeDaLinha: (l) => iconeDePortador(l.valores.encaixe ?? ''),
  },
  cinto: { icone: iconeDoMenu('saco') },
  pericias: { icone: iconeDoMenu('espada'), iconeDaLinha: (l) => iconeDePericia(l.valores.pericia ?? '') },
  teste: { icone: iconeDoMenu('dado'), iconeDaLinha: (l) => iconeDeAcao(l.valores.acao ?? '') },
  progressao: { icone: iconeDerivado('xp-do-nivel') },
  acoes: { icone: iconeDoMenu('fluxo'), iconeDaLinha: (l) => iconeDeAcao(l.valores.acao ?? '') },
  conflitos: { icone: iconeDoMenu('alvo') },
};

export const SECOES: Record<string, SecaoDeQuadros> = Object.fromEntries(
  Object.entries(SECOES_BASE).map(([chave, s]) => [chave, { ...s, ...ICONES[chave] }])
);

/** Os macro-títulos da aba Beta/Season 1 — a ordem dos quadros é a de sempre. */
export const GRUPOS: GrupoDeQuadros[] = [
  { id: 'ficha', titulo: 'A ficha', quadros: ['atributos', 'custo-de-atributo'] },
  { id: 'inventario', titulo: 'Inventário e equipamento', quadros: ['inventario-e-encaixes', 'cinto'] },
  { id: 'pericias', titulo: 'Perícias, teste e progressão', quadros: ['pericias', 'teste', 'progressao'] },
  { id: 'acoes', titulo: 'Ações', quadros: ['acoes'] },
  { id: 'disputa', titulo: 'Conflitos e decisões', quadros: ['conflitos'] },
];
